import clsx from 'clsx';
import {
  type ChangeEvent,
  type CSSProperties,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BiBriefcase,
  BiCalendar,
  BiCheck,
  BiChevronLeft,
  BiChevronRight,
  BiCompass,
  BiDish,
  BiEnvelope,
  BiFile,
  BiGroup,
  BiListCheck,
  BiSend,
  BiUser,
  BiWrench,
} from 'react-icons/bi';
import { FaAnchor } from 'react-icons/fa';
import { FiArrowUpRight, FiCheck, FiChevronDown, FiPlus, FiX } from 'react-icons/fi';
import { TbBrandSafari, TbGift, TbMessage } from 'react-icons/tb';
import { isValidPhoneNumber } from 'react-phone-number-input';

import logoInk from '@/assets/vh-black-name.svg';
import logoWhite from '@/assets/vh-white-name.svg';
import {
  AllergiesDietPicker,
  Avatar,
  canScanDocuments,
  Card,
  ChoiceCards,
  ProgressBar,
  SegmentedControl,
  SuccessCheck,
  type Tab,
  Tabs,
  TextLink,
} from '@/components';
import { AvatarUploader } from '@/components/AvatarUploader/AvatarUploader';
import { BasicLayout } from '@/components/BasicLayouts/BasicLayouts';
import { Button, ButtonGhost, ButtonOutlined } from '@/components/Button';
import { ConfettiCanvas } from '@/components/ConfettiCanvas/ConfettiCanvas';
import EmailConflictModal from '@/components/CreateSheet/CreateVesselSheet/EmailConflictModal';
import { AddressInput, Input, PhoneInput, Select } from '@/components/Inputs';
import { DocumentDropzone, FileRow } from '@/components/ProfileDoc';
import { RichTextArea } from '@/components/RichTextArea/RichTextArea';
import { UnitInput } from '@/components/UnitInput/UnitInput';
import { formatRole } from '@/components/Widget/WhosOnBoardWidget/WhosOnBoardWidget.constants';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import { positionOptions } from '@/pages/Crew/crewUtils';
import { toVesselAutofill } from '@/pages/Vessels/vesselDocumentAutofill';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import { WORK_TYPE_OPTIONS, type WorkType } from '@/store/crew/crew.types';
import { inviteCodeErrorMessage } from '@/store/invites/invites.mappers';
import { useJoinByInviteCodeMutation } from '@/store/invites/invites.store';
import type { JoinedVessel } from '@/store/invites/invites.types';
import {
  type AllergiesDiet,
  dietCount,
  dietGroup,
  type DietGroupId,
  EMPTY_ALLERGIES_DIET,
  EMPTY_DIET_ANSWER,
} from '@/store/persons/allergiesDiet';
import { compressImage } from '@/utils/compressImage';
import { extractAI } from '@/utils/extractAI';
import { CV_AUTOFILL_ENABLED, INVITE_CODES_ENABLED } from '@/utils/featureFlags';
import { toast } from '@/utils/toast';
import { convertLength, round1 } from '@/utils/unitConversion';
import { clearVesselCode, getVesselCode } from '@/utils/vesselCode';

import '@/design-system/animations.scss';
import {
  AVAILABILITY_OPTIONS,
  type AvailableFrom,
  type Choice,
  CREW_ROLES_OPTS,
  CV_TYPES,
  EMPTY_FORM,
  FLOW,
  type FlowStep,
  type Form,
  type IntentId,
  INTENTS,
  INVITE_COPY,
  INVITE_VIA,
  type InviteVia,
  JOB_PATHS,
  type JobPath,
  MAX_JOB_ROLES,
  type PlanId,
  PRICING_URL,
  REQUIRED_FIELDS,
  type Role,
  ROLES,
  SERVICE_TYPE_CHIPS,
  STEP_HINT,
  STEP_TITLE,
  type StepId,
  TRIAL_INCLUDES,
  VESSEL_NEEDS,
} from './OnboardingWizard.constants';
import {
  type OnboardingData,
  type ServiceLocation,
  useOnboardingIdentity,
  useOnboardingSubmit,
  useVesselEmailConflict,
} from './OnboardingWizard.logic';
import s from './OnboardingWizard.module.scss';
import {
  Chip,
  FeatureGrid,
  FinishSetup,
  NextStepTiles,
  type SetupTask,
} from './OnboardingWizard.parts';

interface ReviewRow {
  label: string;
  value: ReactNode;
}

/** A step that can be filled from a document (the vessel's papers, a CV) or typed in. */
type DocumentMode = 'scan' | 'manual';

type VesselScan = { status: 'idle' | 'reading' | 'done' | 'failed'; file?: File };

const DOCUMENT_MODE_TABS: readonly Tab<DocumentMode>[] = [
  { label: 'Autofill', value: 'scan' },
  { label: 'Enter Manually', value: 'manual' },
];

/** Where the job seeker's finish screen sends them first. */
type CrewNextStep = 'cv' | 'port' | 'community';

/** "Steward", "Steward or Chef", "Captain, Steward or Chef" — a list as a sentence says it. */
const listOf = (items: readonly string[], last: string) =>
  items.length < 2
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} ${last} ${items[items.length - 1]}`;

const VESSEL_DOCUMENT_TYPES = '.pdf,.jpg,.jpeg,.png,.heic';

// Same loose check the sign-up flow uses (Signup/SignupPassword.tsx).
const isEmail = (v: string) => /\S+@\S+\.\S+/.test(v);

export default function OnboardingWizard() {
  const identity = useOnboardingIdentity();
  const vesselEmail = useVesselEmailConflict();
  // Creating the vessel makes it the active account, so its id is the one just made.
  const vesselId = useActiveAccount((s) => s.serviceId);
  const { lengthUnit } = useUnitPreferences();

  // A vessel code share link opens the wizard on Join your vessel with the code filled in;
  // Back still leads to the other paths.
  const [linkCode] = useState(() => (INVITE_CODES_ENABLED ? getVesselCode() : null));
  const [intent, setIntent] = useState<IntentId | null>(linkCode ? 'job' : null);
  const { submit, submitting, error, goToDashboard, leaveOnboarding, skipOnboarding } =
    useOnboardingSubmit(intent);
  // ['goal', 'joinVessel'] after the intent screen, so Join your vessel is step 2.
  const [stepIndex, setStepIndex] = useState(linkCode ? 2 : 0);
  const [finished, setFinished] = useState(false);
  const [confettiKey, setConfettiKey] = useState<string | null>(null);
  // The finish opens on the setup takeover; the success screen follows when it is done.
  const [settingUp, setSettingUp] = useState(false);

  const [role, setRole] = useState<Role | null>(null);
  const [jobPath, setJobPath] = useState<JobPath | null>(linkCode ? 'team' : null);
  const joinByCode = useJoinByInviteCodeMutation();
  const [joined, setJoined] = useState<JoinedVessel | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  // Job-seeker crew profile: only what a captain filters on — the rest waits for the profile.
  const [positions, setPositions] = useState<string[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [availableFrom, setAvailableFrom] = useState<AvailableFrom | null>(null);
  const [crewMode, setCrewMode] = useState<DocumentMode>(CV_AUTOFILL_ENABLED ? 'scan' : 'manual');
  const [serviceSelectedCategories, setServiceSelectedCategories] = useState<string[]>([]);
  const [serviceLogo, setServiceLogo] = useState<File | null>(null);
  const [cv, setCv] = useState<File | null>(null);

  const [form, setForm] = useState<Form>(() =>
    linkCode ? { ...EMPTY_FORM, vesselCode: linkCode } : EMPTY_FORM,
  );

  // Wizard steps mount one at a time, so per-field auto-conversion inside
  // UnitInput cannot reach a value entered on an unmounted step. Convert every
  // unit-bearing wizard field here, in one place, whenever the length unit
  // changes; the UnitInputs below opt out via noAutoConvert so the value is
  // not converted twice.
  const prevLengthUnitRef = useRef(lengthUnit);
  useEffect(() => {
    const prev = prevLengthUnitRef.current;
    if (prev === lengthUnit) return;
    prevLengthUnitRef.current = lengthUnit;
    const conv = (v: string) => {
      const parsed = parseFloat(v);
      return isNaN(parsed) ? v : String(round1(convertLength(parsed, prev, lengthUnit)));
    };
    setForm((f) => ({
      ...f,
      vesselLength: conv(f.vesselLength),
      marinaMaxLength: conv(f.marinaMaxLength),
      marinaMaxBeam: conv(f.marinaMaxBeam),
      marinaDraft: conv(f.marinaDraft),
    }));
  }, [lengthUnit]);

  const [needs, setNeeds] = useState<string[]>([]);
  const [crewRoles, setCrewRoles] = useState<string[]>([]);
  /* A guest is asked only what the crew must act on: allergies, then dietary exclusions. */
  const [allergiesDiet, setAllergiesDiet] = useState<AllergiesDiet>(EMPTY_ALLERGIES_DIET);
  const allergiesDietCount = dietCount(allergiesDiet.allergy) + dietCount(allergiesDiet.diet);
  const [locations, setLocations] = useState<string[]>(['']);
  // Full geocoded place per address row (aligned by index to `locations`); null when the
  // picker couldn't resolve the typed text. First non-null seeds create, the rest are POSTed.
  const [servicePlaces, setServicePlaces] = useState<Array<ServiceLocation | null>>([null]);

  // ponytail: not revoking the blob URL — one-shot wizard, the leak is negligible.
  const serviceLogoUrl = useMemo(
    () => (serviceLogo ? URL.createObjectURL(serviceLogo) : null),
    [serviceLogo],
  );

  const updateAddress = (i: number, val: string, place: ServiceLocation | null) => {
    setLocations((l) => l.map((a, idx) => (idx === i ? val : a)));
    setServicePlaces((p) => p.map((pl, idx) => (idx === i ? place : pl)));
  };
  const addAddress = () => {
    setLocations((l) => [...l, '']);
    setServicePlaces((p) => [...p, null]);
  };
  const removeAddress = (i: number) => {
    setLocations((l) => l.filter((_, idx) => idx !== i));
    setServicePlaces((p) => p.filter((_, idx) => idx !== i));
  };

  const setField = (k: keyof Form) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSelect = (k: keyof Form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (list: string[], setList: Dispatch<SetStateAction<string[]>>, id: string) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  // Vessel adds crew steps when crew management is selected.
  // Job branches on the goal step: a crew profile, or a vessel code. Until a goal is picked
  // the rail shows the job-seeker path, the one most people take.
  const steps = useMemo<FlowStep[]>(() => {
    if (!intent) return [];
    if (intent === 'vessel') return ['role', 'vessel', 'invite', 'subscription'];
    if (intent === 'services') {
      const flow: FlowStep[] = ['serviceProfile', 'serviceTypeChips'];
      // Marina chip enables the marina sub-resource → collect berth details (POST /{id}/marina).
      if (serviceSelectedCategories.includes('marina')) flow.push('marinaInfo');
      flow.push('serviceContact', 'location', 'review');
      return flow;
    }
    if (intent === 'job') {
      // With invitation codes paused there is nothing to join by, so the goal step would offer a
      // single answer: the job seeker goes straight to the crew profile.
      if (!INVITE_CODES_ENABLED) return ['crewProfile'];
      // Joining by code ends on the vessel itself: its plan is the vessel's, so there is no
      // plan step, and no crew profile is made for somebody who is not looking for work.
      return jobPath === 'team' ? ['goal', 'joinVessel'] : ['goal', 'crewProfile'];
    }
    return [...FLOW.guest];
  }, [intent, jobPath, serviceSelectedCategories]);

  // Clamp when the flow shrinks (e.g. user deselects crew after advancing).
  // Adjusting during render is React's recommended alternative to a sync effect.
  if (stepIndex > steps.length) {
    setStepIndex(steps.length);
  }

  const totalSteps = 1 + steps.length;
  const currentStepId: StepId = stepIndex === 0 ? 'intent' : steps[stepIndex - 1];
  const isSubscription = currentStepId === 'subscription';
  // Only vessel flows end on the plan step; the free ones submit from their own last step.
  const isLastStep = stepIndex > 0 && stepIndex === steps.length;

  const [hasInvitee, setHasInvitee] = useState<boolean | null>(null);
  const [inviteVia, setInviteVia] = useState<InviteVia>('email');
  const [inviteContact, setInviteContact] = useState('');
  const [invitedTo, setInvitedTo] = useState('');
  const inviteFor = role === 'captain' ? 'owner' : 'captain';
  const inviteCopy = INVITE_COPY[inviteFor];
  const inviteTo = inviteContact.trim();
  const inviteValid =
    inviteVia === 'email'
      ? isEmail(inviteTo) && inviteTo.toLowerCase() !== identity.email.toLowerCase()
      : isValidPhoneNumber(inviteTo);
  const inviteSent = hasInvitee === true && invitedTo !== '';
  const ownerHasPlan = intent === 'vessel' && role === 'captain' && inviteSent;

  const isChoiceStep =
    currentStepId === 'intent' ||
    currentStepId === 'role' ||
    currentStepId === 'goal' ||
    (currentStepId === 'invite' && hasInvitee !== true);
  // Guests, job-seekers and service providers land on the free Harbor tier only, without
  // being shown a plan step: there is nothing for them to pick.
  const isFreeTier = intent === 'guest' || intent === 'job' || intent === 'services';
  // The plan we hand the user — free Harbor, or Command for the 3-month gifted trial.
  const grantedPlan: PlanId = isFreeTier ? 'harbor' : 'command';
  // The gifted Command trial adds Compare Plans beside its CTA.
  const isTrialStep = isSubscription && !ownerHasPlan;

  const railRows = useMemo(
    () => [
      { id: 'intent', name: 'Getting started', hint: 'How you’ll use VesselHaven' },
      ...steps.map((id) => ({
        id,
        name: id === 'invite' ? inviteCopy.title : STEP_TITLE[id],
        hint: STEP_HINT[id],
      })),
    ],
    [steps, inviteCopy.title],
  );
  const [railOpen, setRailOpen] = useState(false);
  const shownStep = stepIndex + 2;
  const shownTotal = totalSteps + 1;
  const stepCountLabel = `Step ${shownStep}${intent ? ` of ${shownTotal}` : ''}`;

  const [vesselMode, setVesselMode] = useState<DocumentMode>('scan');
  const [vesselScan, setVesselScan] = useState<VesselScan>({ status: 'idle' });

  const readVesselDocument = async (file: File) => {
    setVesselScan({ status: 'reading', file });
    try {
      const formData = new FormData();
      formData.append('file', await compressImage(file));
      const result = await extractAI({
        formData,
        type: 'vessel',
        method: 'extract',
        suppressErrorToast: true,
      });
      const found = toVesselAutofill(result?.status === 200 ? result.data?.vessel : null);
      if (!found) {
        setVesselScan({ status: 'failed', file });
        return;
      }
      setForm((f) => ({
        ...f,
        vesselName: f.vesselName.trim() ? f.vesselName : (found.vessel.vessel_name ?? ''),
        vesselMake: found.vessel.make ?? f.vesselMake,
        vesselModel: found.vessel.model ?? f.vesselModel,
        vesselLength:
          found.vessel.length_overall != null
            ? String(found.vessel.length_overall)
            : f.vesselLength,
      }));
      setVesselScan({ status: 'done', file });
      toast.success('Details filled in from your document');
    } catch {
      setVesselScan({ status: 'failed', file });
    }
  };

  const vesselLengthError =
    form.vesselLength !== '' && !(Number(form.vesselLength) >= 0)
      ? 'Please enter the length as a number.'
      : undefined;
  const yearsExpError =
    form.yearsExp !== '' && (!Number.isInteger(Number(form.yearsExp)) || Number(form.yearsExp) < 0)
      ? 'Please enter your years of experience as a whole number.'
      : undefined;
  const jobRolesFull = positions.length >= MAX_JOB_ROLES;

  const canNext =
    currentStepId === 'intent'
      ? Boolean(intent)
      : currentStepId === 'role'
        ? Boolean(role)
        : currentStepId === 'goal'
          ? Boolean(jobPath)
          : currentStepId === 'subscription'
            ? // Nothing to pick — the plan is granted, the step only has to be acknowledged.
              true
            : currentStepId === 'crewProfile'
              ? // The role is the one answer the profile cannot do without; a CV is optional.
                positions.length > 0 && !yearsExpError
              : currentStepId === 'joinVessel'
                ? form.vesselCode.trim() !== ''
                : currentStepId === 'serviceContact'
                  ? // contact_info feeds the provider profile — require a real email + phone
                    isEmail(form.contactEmail) && isValidPhoneNumber(form.contactPhone)
                  : currentStepId === 'vessel'
                    ? (REQUIRED_FIELDS[currentStepId] ?? []).every((k) => form[k].trim()) &&
                      !vesselLengthError
                    : currentStepId === 'invite'
                      ? hasInvitee === false || inviteSent || inviteValid
                      : (REQUIRED_FIELDS[currentStepId] ?? []).every((k) => form[k].trim());

  // The crew steps keep Continue live and say what is missing on press: a dimmed pill reads
  // as enabled on the wizard's footer, so a dead button left people stuck without a reason.
  const explainsMissing = currentStepId === 'crewProfile' || currentStepId === 'joinVessel';
  const [triedStep, setTriedStep] = useState<StepId | null>(null);
  const tried = triedStep === currentStepId;
  const roleError = tried && positions.length === 0 ? 'Please pick at least one role.' : undefined;
  const vesselCodeError =
    tried && !form.vesselCode.trim()
      ? 'Please enter your vessel code to continue.'
      : (joinError ?? undefined);

  const collectData = (answers: AllergiesDiet = allergiesDiet): OnboardingData => ({
    intent,
    role,
    jobPath,
    form,
    serviceCategories: serviceSelectedCategories,
    serviceLogo,
    cv,
    allergiesDiet: answers,
    locations,
    serviceLocations: servicePlaces.filter((p): p is ServiceLocation => p !== null),
    selectedPlan: grantedPlan,
    vesselEmail: vesselEmail.email,
    positions,
    workTypes,
    availableFrom,
    invite: intent === 'vessel' && inviteSent ? { contact: invitedTo, role: inviteFor } : null,
  });

  const goNext = async () => {
    if (submitting || vesselEmail.checking || joinByCode.isPending) return;
    if (!canNext) {
      if (explainsMissing) setTriedStep(currentStepId);
      return;
    }
    // The vessel's internal_email is derived from its name and must be unique backend-side.
    // Settle any conflict here rather than letting create fail on the last step.
    if (currentStepId === 'vessel' && !(await vesselEmail.ensureAvailable(form.vesselName))) return;
    if (currentStepId === 'invite' && hasInvitee === true && !inviteSent) setInvitedTo(inviteTo);
    // The code joins on Continue: the role comes with it, so the step is the last one.
    if (currentStepId === 'joinVessel') {
      try {
        setJoined(await joinByCode.mutateAsync(form.vesselCode));
      } catch (err) {
        console.error(err);
        setJoinError(inviteCodeErrorMessage(err));
        return;
      } finally {
        // Tried once, whatever the answer: the dashboard must not offer it again.
        clearVesselCode();
      }
      setFinished(true);
      setConfettiKey('done-join');
      return;
    }
    if (isLastStep) {
      await finish(collectData());
      return;
    }
    setTriedStep(null);
    setStepIndex((i) => i + 1);
  };
  const finish = async (data: OnboardingData) => {
    const ok = await submit(data);
    if (!ok) return; // stay on the step; error is surfaced in the footer
    setFinished(true);
    // The success screen bursts once its setup takeover hands over, not under it.
    setSettingUp(true);
  };
  /* "Skip for now" on a guest's allergies or diet step leaves that question unanswered
     and moves on, finishing on the last one. */
  const guestDietGroup: DietGroupId | null =
    currentStepId === 'guestAllergies' ? 'allergy' : currentStepId === 'guestDiet' ? 'diet' : null;
  const skipGuestDietStep = async () => {
    if (!guestDietGroup || submitting) return;
    const answers = { ...allergiesDiet, [guestDietGroup]: EMPTY_DIET_ANSWER };
    setAllergiesDiet(answers);
    if (isLastStep) await finish(collectData(answers));
    else setStepIndex((i) => i + 1);
  };
  const goBack = () => {
    setTriedStep(null);
    setStepIndex((i) => Math.max(0, i - 1));
  };
  const selectAndAdvance =
    <T,>(setter: (id: T) => void) =>
    (id: T) => {
      setter(id);
      setStepIndex((i) => i + 1);
    };

  // "Not sure yet" / "Skip for now" → straight to the dashboard, no interstitial.
  const skipSetup = () => skipOnboarding();

  const reviewRows = (): ReviewRow[] => {
    const intentLabel = INTENTS.find((i) => i.id === intent)?.title;
    if (intent === 'vessel') {
      const rows: ReviewRow[] = [
        { label: 'Goal', value: intentLabel },
        { label: 'Role', value: ROLES.find((r) => r.id === role)?.title ?? '-' },
        { label: 'Vessel', value: form.vesselName || '-' },
        { label: 'Make', value: form.vesselMake || '-' },
        { label: 'Length', value: form.vesselLength ? `${form.vesselLength} ${lengthUnit}` : '-' },
        { label: 'Tools selected', value: `${needs.length} of ${VESSEL_NEEDS.length}` },
      ];
      if (needs.includes('crew')) {
        rows.push({ label: 'Crew size', value: form.crewCount || '-' });
        rows.push({
          label: 'Crew roles',
          value: crewRoles.length ? `${crewRoles.length} selected` : '-',
        });
      }
      return rows;
    }
    if (intent === 'services') {
      const rows: ReviewRow[] = [
        { label: 'Goal', value: intentLabel },
        { label: 'Business', value: form.businessName || '-' },
        {
          label: 'Service types',
          value: serviceSelectedCategories.length
            ? `${serviceSelectedCategories.length} selected`
            : '-',
        },
        {
          label: 'Locations',
          value: locations.filter(Boolean).length
            ? `${locations.filter(Boolean).length} address${locations.filter(Boolean).length > 1 ? 'es' : ''}`
            : '-',
        },
      ];
      if (serviceSelectedCategories.includes('marina')) {
        rows.push({ label: 'Marina', value: 'Berth details added' });
      }
      return rows;
    }
    if (intent === 'guest') {
      // 'Vessel code' row omitted — the vesselCode step is hidden for guests (see FLOW),
      // so it can never be filled. Restore alongside that step.
      return [
        { label: 'Goal', value: intentLabel },
        {
          label: 'Allergies & Diet',
          value: allergiesDietCount > 0 ? `${allergiesDietCount} selected` : '-',
        },
      ];
    }
    // The crew paths have no review step: Crew profile goes straight to the plan.
    return [];
  };

  /* ── Job seeker's finish: what is ready, then a first thing to do ── */
  const roleNames = positionOptions.filter((o) => positions.includes(o.value)).map((o) => o.label);
  const crewSetupTasks: SetupTask[] = [
    { doing: 'Creating your account', done: 'Account created' },
    { doing: 'Building your crew profile', done: 'Crew profile built' },
    cv
      ? { doing: 'Adding your CV', done: 'CV added' }
      : { doing: 'Preparing your CV page', done: 'CV page ready to fill in' },
  ];
  const accountTask: SetupTask = { doing: 'Creating your account', done: 'Account created' };
  const vesselSetupTasks: SetupTask[] = [
    accountTask,
    {
      doing: `Setting up ${form.vesselName.trim() || 'your vessel'}`,
      done: `${form.vesselName.trim() || 'Your vessel'} set up`,
    },
    { doing: 'Preparing your dashboard', done: 'Dashboard ready' },
    ...(inviteSent
      ? [
          inviteFor === 'owner'
            ? { doing: 'Inviting the owner', done: 'Owner invited' }
            : { doing: 'Inviting your captain', done: 'Captain invited' },
        ]
      : []),
    ...(ownerHasPlan
      ? []
      : [{ doing: 'Starting your free Command trial', done: 'Free Command trial started' }]),
  ];
  const guestSetupTasks: SetupTask[] = [
    accountTask,
    ...(allergiesDietCount
      ? [{ doing: 'Sharing your allergies & diet', done: 'Allergies & diet shared' }]
      : []),
    ...(form.vesselCode
      ? [{ doing: 'Requesting your vessel connection', done: 'Vessel connection requested' }]
      : []),
  ];
  const serviceCount = serviceSelectedCategories.length;
  const serviceSetupTasks: SetupTask[] = [
    accountTask,
    { doing: 'Publishing your business profile', done: 'Business profile published' },
    serviceCount
      ? {
          doing: `Listing your ${serviceCount === 1 ? 'service' : 'services'}`,
          done: `${serviceCount} ${serviceCount === 1 ? 'service' : 'services'} listed`,
        }
      : { doing: 'Saving your service types', done: 'Service types saved' },
    { doing: 'Adding your contact details', done: 'Contact details added' },
  ];
  const setupTasks =
    intent === 'job'
      ? crewSetupTasks
      : intent === 'guest'
        ? guestSetupTasks
        : intent === 'services'
          ? serviceSetupTasks
          : vesselSetupTasks;
  const crewFinishLines = [
    'Your account is ready.',
    'Your crew profile is set up.',
    ...(cv ? ['Your CV is on your profile.'] : []),
  ];
  const crewNextSteps: Choice<CrewNextStep>[] = [
    {
      id: 'cv',
      title: cv ? 'Polish your CV' : 'Finish your CV',
      desc: 'Stand out to captains',
      Icon: BiFile,
    },
    { id: 'port', title: 'Meet crew in port', desc: 'Daywork, meetups and news', Icon: BiCompass },
    {
      id: 'community',
      title: 'Join a crew community',
      desc: 'Meet crew in your region',
      Icon: BiGroup,
    },
  ];
  const crewNextStepTo: Record<CrewNextStep, string> = {
    // The profile exists by now, so the CV lives on the crew page, as My Profile links it.
    cv: `/crew/${identity.personId}`,
    port: '/discover',
    community: '/discover',
  };

  const vesselLabel = form.vesselName.trim() || 'your vessel';
  const trialEnds = new Date();
  trialEnds.setMonth(trialEnds.getMonth() + 3);
  const trialEndsOn = trialEnds.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const shortDate = (d: Date) => d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  // The phone has room for the milestone, not the sentence under it: shorter labels, no blurbs.
  const isMobile = useIsMobile();
  const trialMilestones = [
    {
      Icon: TbGift,
      label: isMobile ? 'Today' : `Today · ${shortDate(new Date())}`,
      title: isMobile ? 'All unlocked' : 'Everything unlocked',
      blurb: 'Every Command feature is yours to use right away.',
    },
    {
      Icon: TbBrandSafari, // the compass needle without tick marks
      label: isMobile ? '3 months' : 'Next 3 months',
      title: 'Make it yours',
      blurb: 'Set up your vessel, invite crew, and run your routines in one place.',
    },
    {
      Icon: TbMessage,
      label: isMobile ? shortDate(trialEnds) : `By ${trialEndsOn}`,
      title: 'We’ll be in touch',
      blurb: 'With a personal offer for your crew. No plan picked? You stay on Harbor (free).',
    },
  ];
  // No first-step tiles for the provider yet: its finish leads to the dashboard alone.
  const finishNext: (Choice & { to: string })[] =
    intent === 'services'
      ? []
      : intent === 'job'
        ? crewNextSteps.map((step) => ({ ...step, to: crewNextStepTo[step.id] }))
        : intent === 'guest'
          ? [
              {
                id: 'plan',
                title: 'See what’s planned',
                desc: 'Trips and events on your vessel',
                Icon: BiCalendar,
                to: '/plan',
              },
              {
                id: 'discover',
                title: 'Discover events',
                desc: 'What’s on near you',
                Icon: BiCompass,
                to: '/plan',
              },
              /* Allergies & diet live in the profile: to check what was just
                 shared, or to answer what was skipped. */
              {
                id: 'allergies',
                title: allergiesDietCount
                  ? 'Check your allergies & diet'
                  : 'Tell the chef what you can’t eat',
                desc: allergiesDietCount
                  ? 'Change them any time'
                  : 'Allergies and dietary exclusions',
                Icon: BiDish,
                to: '/my-profile?tab=allergies',
              },
            ]
          : role === 'captain'
            ? [
                ...(ownerHasPlan
                  ? []
                  : [
                      {
                        id: 'owner',
                        title: 'Invite the owner',
                        desc: 'Let them follow what’s happening aboard',
                        Icon: BiBriefcase,
                        to: vesselId ? `/vessels/${vesselId}#ownership` : '/vessels',
                      },
                    ]),
                {
                  id: 'crew',
                  title: 'Invite your crew',
                  desc: 'Get your crew aboard',
                  Icon: BiGroup,
                  to: '/manage/crew',
                },
                {
                  id: 'equipment',
                  title: 'Add your equipment',
                  desc: 'Engines, tenders and systems you look after',
                  Icon: BiWrench,
                  to: '/maintain',
                },
                {
                  id: 'tasks',
                  title: 'Set today’s tasks',
                  desc: 'Share out the jobs aboard',
                  Icon: BiListCheck,
                  to: '/tasks',
                },
              ].slice(0, 3)
            : [
                {
                  id: 'crew',
                  title: 'Invite your crew',
                  desc: 'Add the people who work aboard',
                  Icon: BiGroup,
                  to: '/manage/crew',
                },
                {
                  id: 'schedule',
                  title: 'Plan maintenance',
                  desc: 'Log the next service or check',
                  Icon: BiWrench,
                  to: '/maintain/schedule',
                },
                {
                  id: 'plan',
                  title: 'Plan a trip or event',
                  desc: 'Put the next outing on the calendar',
                  Icon: BiCalendar,
                  to: '/plan',
                },
              ];
  const finishReadyLines =
    intent === 'services'
      ? [
          'Your account is ready.',
          `${form.businessName.trim() || 'Your business'} is listed for owners and captains.`,
          serviceCount
            ? `${serviceCount} ${serviceCount === 1 ? 'service' : 'services'} on your profile.`
            : 'Please add your services from My Services.',
          'Clients can reach you by phone and email.',
        ]
      : intent === 'job'
        ? crewFinishLines
        : intent === 'guest'
          ? [
              'Your account is ready.',
              ...(allergiesDietCount ? ['Your allergies & diet are shared with the crew.'] : []),
              form.vesselCode
                ? 'Vessel connection requested.'
                : 'You can connect to a vessel from your dashboard.',
            ]
          : [
              'Your account is ready.',
              `${vesselLabel} is set up, with you as ${role === 'captain' ? 'captain' : 'owner'}.`,
              role === 'captain'
                ? 'Your dashboard is ready to run the vessel.'
                : 'Your dashboard is ready.',
              ownerHasPlan
                ? 'The owner will choose a plan when they join.'
                : `Command is free until ${trialEndsOn}`,
            ];
  const finishLede =
    intent === 'services'
      ? 'Owners and captains can now find you, request quotes and pay you in-app.'
      : intent === 'job'
        ? `Your crew profile is ready. Captains hiring ${listOf(roleNames, 'or') || 'crew'} can now find you.`
        : intent === 'guest'
          ? form.vesselCode
            ? `Your account is set up and you’ve asked to join your vessel.${allergiesDietCount ? ' The crew will know what you can’t eat.' : ''}`
            : 'Your account is set up. Connect to a vessel whenever you’re ready.'
          : ownerHasPlan
            ? `Your account is set up and ${vesselLabel} is ready for you.`
            : `Your account is set up and ${vesselLabel} is ready. Your 3 free months of Command have started.`;

  const ctaLabel = isSubscription
    ? ownerHasPlan
      ? 'Get Started'
      : 'Start 3 Months Free'
    : currentStepId === 'guestAllergies'
      ? `Next: ${dietGroup('diet').label}`
      : currentStepId === 'guestDiet'
        ? 'Save & Finish'
        : isLastStep
          ? 'Get Started'
          : 'Continue';

  /* One view for both of a guest's must-know steps: the group's question, then its picker. */
  const guestDietStep = (group: DietGroupId) => {
    const g = dietGroup(group);
    return (
      <div className={s.stepContent} key={group}>
        <h2 className={s.stepTitle}>{g.question}</h2>
        <p className={s.stepSub}>{g.lead} Pick all that apply.</p>
        <AllergiesDietPicker
          group={group}
          value={allergiesDiet[group]}
          onChange={(next) => setAllergiesDiet((answers) => ({ ...answers, [group]: next }))}
        />
      </div>
    );
  };

  /* ── Step content — one entry per step id ────────────────────── */
  const stepViews: Record<StepId, () => ReactNode> = {
    intent: () => (
      <div className={s.stepContent} key="intent">
        <h2 className={s.stepTitle}>
          {identity.firstName
            ? `Welcome to VesselHaven, ${identity.firstName}`
            : 'Welcome to VesselHaven'}
        </h2>
        <p className={s.stepSub}>
          We’re glad you’re here. A few quick questions to set up your dashboard.
        </p>
        <ChoiceCards
          items={INTENTS}
          selectedId={intent}
          onSelect={selectAndAdvance<IntentId>(setIntent)}
          footer={
            <TextLink className={s.lookAround} onClick={skipSetup}>
              Not sure yet? Look around first →
            </TextLink>
          }
        />
      </div>
    ),

    role: () => (
      <div className={s.stepContent} key="role">
        <h2 className={s.stepTitle}>What’s your role aboard?</h2>
        <p className={s.stepSub}>This tailors the tools and permissions you’ll see.</p>
        <ChoiceCards items={ROLES} selectedId={role} onSelect={selectAndAdvance<Role>(setRole)} />
      </div>
    ),

    invite: () => (
      <div className={s.stepContent} key={`invite-${inviteFor}`}>
        <h2 className={s.stepTitle}>{inviteCopy.heading}</h2>
        <p className={s.stepSub}>{inviteCopy.lede}</p>
        <ChoiceCards
          items={[
            {
              id: 'yes',
              title: 'Yes, invite them',
              desc: inviteCopy.yes.hint,
              Icon: inviteCopy.yes.Icon,
            },
            { id: 'no', title: inviteCopy.no.title, desc: inviteCopy.no.hint, Icon: BiUser },
          ]}
          selectedId={hasInvitee === null ? null : hasInvitee ? 'yes' : 'no'}
          onSelect={(id) => {
            setHasInvitee(id === 'yes');
            if (id === 'no') setStepIndex((i) => i + 1);
          }}
        />
        {hasInvitee === true && (
          <Card padding="lg" className={s.invite}>
            {inviteSent ? (
              <div className={s.inviteSent}>
                <SuccessCheck size={40} label="Invite sent" />
                <span className={s.inviteText}>
                  <span className={s.inviteTitle}>Invite sent</span>
                  <span className={s.inviteHint}>
                    To {invitedTo}. {inviteCopy.sent}
                  </span>
                  <TextLink className={s.inviteChange} onClick={() => setInvitedTo('')}>
                    Change
                  </TextLink>
                </span>
              </div>
            ) : (
              <>
                <div className={s.inviteHead}>
                  <span className={s.inviteText}>
                    <span className={s.inviteTitle}>{inviteCopy.card}</span>
                    <span className={s.inviteHint}>We’ll only use this to send the invite.</span>
                  </span>
                  <SegmentedControl
                    size="sm"
                    as="radio"
                    options={INVITE_VIA}
                    value={inviteVia}
                    onChange={(via: InviteVia) => {
                      setInviteVia(via);
                      setInviteContact('');
                    }}
                    aria-label="Send the invite by"
                  />
                </div>
                <div className={s.inviteRow}>
                  {inviteVia === 'email' ? (
                    <Input
                      className={s.inviteField}
                      label=""
                      aria-label={`${inviteCopy.field} email`}
                      startIcon={<BiEnvelope size={18} />}
                      type="email"
                      inputMode="email"
                      autoComplete="off"
                      placeholder={inviteCopy.placeholder}
                      value={inviteContact}
                      onChange={(e) => setInviteContact(e.target.value)}
                    />
                  ) : (
                    <PhoneInput
                      className={s.inviteField}
                      value={inviteContact}
                      onChange={(v) => setInviteContact(v ?? '')}
                    />
                  )}
                  <ButtonOutlined
                    type="button"
                    disabled={!inviteValid}
                    onClick={() => setInvitedTo(inviteTo)}
                  >
                    <BiSend aria-hidden="true" /> Send invite
                  </ButtonOutlined>
                </div>
              </>
            )}
          </Card>
        )}
      </div>
    ),

    vessel: () => (
      <div className={s.stepContent} key="vessel">
        <h2 className={s.stepTitle}>Tell us about your vessel</h2>
        <p className={s.stepSub}>
          Please start with the name you use aboard. Upload a document to fill in the rest, or type
          it in.
        </p>
        <Input
          label="Vessel Name"
          mandatory
          value={form.vesselName}
          onChange={setField('vesselName')}
          placeholder="e.g. Andiamo"
        />
        <Tabs
          className={s.modeTabs}
          tabs={DOCUMENT_MODE_TABS}
          activeTab={vesselMode}
          onChange={setVesselMode}
        />

        {vesselMode === 'scan' && vesselScan.status !== 'done' && (
          <>
            {vesselScan.status === 'failed' && (
              <p className={s.scanError} role="alert">
                Couldn’t read {vesselScan.file?.name ?? 'this document'}. Please try another file,
                or enter the details manually.
              </p>
            )}
            <DocumentDropzone
              title="Drag a document here"
              hint="For example, the registration certificate (PDF, JPG or PNG). VesselHaven fills in the details."
              privacy="We only read the name, make, model and length."
              accept={VESSEL_DOCUMENT_TYPES}
              camera={canScanDocuments()}
              reading={vesselScan.status === 'reading' ? vesselScan.file?.name : undefined}
              onPick={(file) => void readVesselDocument(file)}
            />
          </>
        )}

        {vesselMode === 'scan' && vesselScan.status === 'done' && vesselScan.file && (
          <FileRow
            name={vesselScan.file.name}
            size={vesselScan.file.size}
            note="The details below come from your document. Please check them."
            replaceAccept={VESSEL_DOCUMENT_TYPES}
            onReplace={(file) => void readVesselDocument(file)}
          />
        )}

        {(vesselMode === 'manual' || vesselScan.status === 'done') && (
          <div className={s.modeFields}>
            <Input
              label="Make"
              value={form.vesselMake}
              onChange={setField('vesselMake')}
              placeholder="e.g. Sunseeker"
            />
            <div className={s.formGrid}>
              <Input
                label="Model"
                value={form.vesselModel}
                onChange={setField('vesselModel')}
                placeholder="Manhattan 68"
              />
              <UnitInput
                kind="length"
                noAutoConvert
                label="Length"
                value={form.vesselLength}
                onChange={(v) => setForm((f) => ({ ...f, vesselLength: v }))}
                placeholder="e.g. 68"
                type="number"
                min={0}
                step="any"
                error={vesselLengthError}
              />
            </div>
          </div>
        )}
      </div>
    ),

    needs: () => (
      <div className={s.stepContent} key="needs">
        <h2 className={s.stepTitle}>VesselHaven can make everything aboard easier.</h2>
        <p className={s.stepSub}>
          Please choose any or all of these features to appear on your dashboard. You can always add
          more later.
        </p>
        <FeatureGrid
          items={VESSEL_NEEDS}
          selected={needs}
          onToggle={(id) => toggle(needs, setNeeds, id)}
        />
      </div>
    ),

    crewInfo: () => (
      <div className={s.stepContent} key="crewInfo">
        <h2 className={s.stepTitle}>Tell us about your crew</h2>
        <p className={s.stepSub}>
          VesselHaven helps you manage roles, schedules and communication.
        </p>
        <div className={s.formGrid}>
          <Input
            label="Number of crew"
            value={form.crewCount}
            onChange={setField('crewCount')}
            placeholder="8"
            type="number"
            min={0}
            step={1}
          />
        </div>
        <p className={s.sectionLabel}>Crew roles aboard</p>
        <FeatureGrid
          items={CREW_ROLES_OPTS}
          selected={crewRoles}
          onToggle={(id) => toggle(crewRoles, setCrewRoles, id)}
        />
      </div>
    ),

    serviceProfile: () => (
      <div className={s.stepContent} key="serviceProfile">
        <h2 className={s.stepTitle}>Tell us about your business</h2>
        <p className={s.stepSub}>
          This is how you&apos;ll appear to vessels searching for services.
        </p>
        <div className={s.serviceProfileLayout}>
          <AvatarUploader
            image={serviceLogoUrl}
            onChange={setServiceLogo}
            firstName={form.businessName}
            lastName=""
            style={{ width: 100, height: 100 }}
          />
          <div className={s.serviceProfileFields}>
            <Input
              label="Business name"
              mandatory
              value={form.businessName}
              onChange={setField('businessName')}
              placeholder="Azure Marine Co."
            />
            <RichTextArea
              label="Description"
              mandatory
              value={form.serviceDescription}
              onChange={setSelect('serviceDescription')}
              placeholder="Hull cleaning and detailing for vessels up to 40 m"
            />
          </div>
        </div>
      </div>
    ),

    serviceTypeChips: () => (
      <div className={s.stepContent} key="serviceTypeChips">
        <h2 className={s.stepTitle}>What type of services do you offer?</h2>
        <p className={s.stepSub}>
          Select all that apply - we&apos;ll match you with the right vessels.
        </p>
        <div className={s.chipList}>
          {SERVICE_TYPE_CHIPS.map(({ id, label, icon }) => {
            const active = serviceSelectedCategories.includes(id);
            return (
              <Chip
                key={id}
                active={active}
                onClick={() => toggle(serviceSelectedCategories, setServiceSelectedCategories, id)}
              >
                {/* selected → Chip shows the check; otherwise show the category icon */}
                {!active &&
                  (id === 'marina' ? (
                    <FaAnchor className={s.chipIcon} />
                  ) : (
                    <img className={s.chipIcon} src={icon} alt="" />
                  ))}
                {label}
              </Chip>
            );
          })}
        </div>
      </div>
    ),

    marinaInfo: () => (
      <div className={s.stepContent} key="marinaInfo">
        <h2 className={s.stepTitle}>Tell us about your marina</h2>
        <p className={s.stepSub}>
          Berth limits and pricing are optional. You can change them later.
        </p>
        <div className={s.formGrid}>
          <UnitInput
            kind="length"
            noAutoConvert
            label="Max Length"
            value={form.marinaMaxLength}
            onChange={(v) => setForm((f) => ({ ...f, marinaMaxLength: v }))}
            placeholder="120"
            type="number"
            min="0"
          />
          <UnitInput
            kind="length"
            noAutoConvert
            label="Max Beam"
            value={form.marinaMaxBeam}
            onChange={(v) => setForm((f) => ({ ...f, marinaMaxBeam: v }))}
            placeholder="30"
            type="number"
            min="0"
          />
          <UnitInput
            kind="length"
            noAutoConvert
            label="Shallowest Draft"
            value={form.marinaDraft}
            onChange={(v) => setForm((f) => ({ ...f, marinaDraft: v }))}
            placeholder="8"
            type="number"
            min="0"
          />
          <Input
            label="Price per foot (daily)"
            value={form.marinaPriceDaily}
            onChange={setField('marinaPriceDaily')}
            placeholder="5"
            suffix="$"
            type="number"
            min="0"
            inputMode="decimal"
          />
          <Input
            label="Price per foot (monthly)"
            value={form.marinaPriceMonthly}
            onChange={setField('marinaPriceMonthly')}
            placeholder="100"
            suffix="$"
            type="number"
            min="0"
            inputMode="decimal"
          />
        </div>
      </div>
    ),

    serviceContact: () => (
      <div className={s.stepContent} key="serviceContact">
        <h2 className={s.stepTitle}>Contact information</h2>
        <p className={s.stepSub}>Vessels will use these details to get in touch with you.</p>
        <div className={s.formGrid}>
          <Input
            label="Business email"
            mandatory
            value={form.contactEmail}
            onChange={setField('contactEmail')}
            placeholder="info@azuremarine.com"
          />
          <PhoneInput
            label="Mobile"
            mandatory
            value={form.contactPhone}
            onChange={(v) => setSelect('contactPhone')(v ?? '')}
          />
          <Input
            label="Website"
            value={form.contactWebsite}
            onChange={setField('contactWebsite')}
            placeholder="www.azuremarine.com"
          />
        </div>
      </div>
    ),

    location: () => (
      <div className={s.stepContent} key="location">
        <h2 className={s.stepTitle}>Where do you operate?</h2>
        <p className={s.stepSub}>Help vessels nearby find your services.</p>
        <div className={s.addressList}>
          {locations.map((addr, i) => (
            <div className={s.addressRow} key={i}>
              <p className={s.addressLabel}>Based in</p>
              <div className={s.addressInputRow}>
                <AddressInput
                  label=""
                  className={s.locationInput}
                  initialInput={addr}
                  onPlaceChange={(place) =>
                    updateAddress(
                      i,
                      place?.formattedAddress ?? '',
                      place?.location
                        ? {
                            name: place.displayName?.text ?? 'Name',
                            address: place.formattedAddress ?? '',
                            latitude: place.location.latitude,
                            longitude: place.location.longitude,
                          }
                        : null,
                    )
                  }
                  placeholder="Port Vauban, Antibes"
                  searchOptions={{
                    includedPrimaryTypes: ['street_address', 'premise', 'establishment'],
                  }}
                />
                {locations.length > 1 && (
                  <button
                    type="button"
                    className={s.addressRemoveBtn}
                    onClick={() => removeAddress(i)}
                  >
                    <FiX size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
          <button type="button" className={s.addAddressBtn} onClick={addAddress}>
            <FiPlus size={15} />
            Add Another Location
          </button>
        </div>
      </div>
    ),

    guestAllergies: () => guestDietStep('allergy'),
    guestDiet: () => guestDietStep('diet'),

    vesselCode: () => (
      <div className={s.stepContent} key="vesselCode">
        <h2 className={s.stepTitle}>Have a vessel code?</h2>
        <p className={s.stepSub}>
          If your captain sent you a code, please enter it here. You can also skip and connect
          later.
        </p>
        <div className={s.vesselCodeBox}>
          <Input
            label="Vessel code"
            value={form.vesselCode}
            onChange={setField('vesselCode')}
            placeholder="VH-2024-AZURE"
          />
        </div>
        <p className={s.fieldNote}>
          Don’t have a code yet? You can connect to a vessel from your dashboard at any time.
        </p>
      </div>
    ),

    goal: () => (
      <div className={s.stepContent} key="goal">
        <h2 className={s.stepTitle}>What would you like to do?</h2>
        <p className={s.stepSub}>
          Please tell us your situation, and VesselHaven will set things up around it.
        </p>
        <ChoiceCards
          items={JOB_PATHS}
          selectedId={jobPath}
          onSelect={selectAndAdvance<JobPath>(setJobPath)}
        />
      </div>
    ),

    // Only what a captain filters on: the roles, and when and how the person wants to work.
    // Work history, certificates and the rest come with the CV or wait for the profile.
    crewProfile: () => (
      <div className={s.stepContent} key="crewProfile">
        <h2 className={s.stepTitle}>Your crew profile</h2>
        <p className={s.stepSub}>
          {CV_AUTOFILL_ENABLED
            ? 'Please pick your role, then add your CV or fill in your experience.'
            : 'Please pick your role, then tell captains when and how you want to work.'}
        </p>
        <Select
          label="Role"
          mandatory
          placeholder="Pick one or more roles"
          options={positionOptions.map((o) => ({
            ...o,
            disabled: jobRolesFull && !positions.includes(o.value),
          }))}
          values={positions}
          onMultipleChange={(list) => setPositions(list.slice(0, MAX_JOB_ROLES))}
          error={roleError}
        />
        {CV_AUTOFILL_ENABLED && (
          <Tabs
            className={s.modeTabs}
            tabs={DOCUMENT_MODE_TABS}
            activeTab={crewMode}
            onChange={setCrewMode}
          />
        )}

        {/* ponytail: the CV is attached as it is — reading it into the profile needs a CV
            type in the extract service, which does not exist yet. Without the autofill tab
            the step asks only for the fields. */}
        {CV_AUTOFILL_ENABLED &&
          crewMode === 'scan' &&
          (cv ? (
            <FileRow
              name={cv.name}
              size={cv.size}
              note="Your CV goes on your profile."
              replaceAccept={CV_TYPES}
              onReplace={setCv}
              onRemove={() => setCv(null)}
            />
          ) : (
            <DocumentDropzone
              title="Drag your CV here"
              hint="PDF, Word or a photo."
              accept={CV_TYPES}
              camera={canScanDocuments()}
              onPick={setCv}
            />
          ))}

        {(crewMode === 'manual' || cv) && (
          <div className={s.modeFields}>
            <div className={s.formGrid}>
              <Input
                label="Years of Experience"
                value={form.yearsExp}
                onChange={setField('yearsExp')}
                placeholder="e.g. 5"
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                error={yearsExpError}
              />
              <Select<AvailableFrom | null>
                label="Available from"
                placeholder="Choose"
                options={AVAILABILITY_OPTIONS}
                value={availableFrom}
                onChange={setAvailableFrom}
              />
            </div>
            {/* Any kind but permanent also makes them open to temporary contracts. */}
            <Select<WorkType>
              label="Type of work"
              placeholder="Choose one or more"
              options={WORK_TYPE_OPTIONS}
              values={workTypes}
              onMultipleChange={setWorkTypes}
            />
            <p className={s.fieldNote}>You can add the rest later in your profile.</p>
          </div>
        )}
      </div>
    ),

    joinVessel: () => (
      <div className={s.stepContent} key="joinVessel">
        <h2 className={s.stepTitle}>Join your vessel</h2>
        <p className={s.stepSub}>Please enter the vessel code from your invitation.</p>
        <div className={s.vesselCodeBox}>
          <Input
            label="Vessel code"
            mandatory
            value={form.vesselCode}
            onChange={(e) => {
              setJoinError(null);
              setField('vesselCode')(e);
            }}
            placeholder="e.g. VH-7K3M-Q9PX"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            error={vesselCodeError}
          />
        </div>
        <p className={s.fieldNote}>
          Your owner or captain sends you a code that works once. You’ll join the vessel as soon as
          you continue.
        </p>
      </div>
    ),

    // The plan is a gift, not a choice: vessel flows are handed a 3-month Command trial.
    subscription: () => {
      if (ownerHasPlan) {
        return (
          <div className={s.stepContent} key="subscription-owner-plan">
            <h2 className={s.stepTitle}>You’re ready to run your vessel</h2>
            <p className={s.stepSub}>
              The owner will choose a plan when they join. You can start running your vessel right
              away.
            </p>
          </div>
        );
      }
      return (
        <div className={clsx(s.stepContent, s.stepContentWide)} key="subscription">
          <h2 className={clsx(s.stepTitle, s.trialTitle)}>
            Your 3 months of full access starts now
          </h2>
          <p className={clsx(s.stepSub, s.trialSub)}>
            Every VesselHaven feature is unlocked starting today. No card, no commitment.
          </p>
          <section className={s.trialTimeline} aria-label="How your trial works">
            <ol className={s.trialSteps}>
              {trialMilestones.map(({ Icon, label, title, blurb }, i) => (
                <li key={title} className={s.trialStep} style={{ '--_i': i } as CSSProperties}>
                  <span className={s.trialMarker} aria-hidden="true">
                    <Icon />
                  </span>
                  <span className={s.trialLabel}>{label}</span>
                  <span className={s.trialName}>{title}</span>
                  {!isMobile && <span className={s.trialBlurb}>{blurb}</span>}
                </li>
              ))}
            </ol>
            {isMobile && (
              <p className={s.trialNote}>
                Before your trial ends we’ll contact you with a personal offer. No plan picked? You
                simply stay on Harbor (free).
              </p>
            )}
          </section>
          <section className={s.trialIncludes} aria-labelledby="onb-trial-includes">
            <div className={s.trialIncludesHead}>
              <h3 className={s.trialIncludesTitle} id="onb-trial-includes">
                Included in your trial
              </h3>
              <span className={s.trialIncludesPlan}>
                {isMobile ? 'Command' : 'Full control · Command'}
              </span>
            </div>
            <ul className={s.trialFeatures}>
              {TRIAL_INCLUDES.map((feature, i) => (
                <li key={feature} style={{ '--_i': i } as CSSProperties}>
                  <span className={s.trialTick} aria-hidden="true">
                    <FiCheck />
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
          </section>
        </div>
      );
    },

    review: () => (
      <div className={s.stepContent} key="review">
        <h2 className={s.stepTitle}>Looks good?</h2>
        <p className={s.stepSub}>Here’s what VesselHaven will set up for you.</p>
        <div className={s.reviewGrid}>
          {reviewRows().map((row) => (
            <div className={s.reviewItem} key={row.label}>
              <span className={s.reviewCheckIcon}>
                <FiCheck size={14} />
              </span>
              <span className={s.reviewLabel}>{row.label}</span>
              <span className={s.reviewValue}>{row.value}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  };

  /* ── Render ──────────────────────────────────────────────────── */
  return (
    <BasicLayout className={s.page}>
      <header className={s.brandBar}>
        <h1 className={s.brand}>
          <span className={s.brandMark}>
            <img className={s.brandWhite} src={logoWhite} alt="" aria-hidden="true" />
            <img className={s.brandInk} src={logoInk} alt="" aria-hidden="true" />
          </span>
          <span className={s.srOnly}>Welcome to VesselHaven</span>
        </h1>
      </header>
      <section className={s.layout}>
        <aside className={clsx(s.sidebar, railOpen && s.sidebarOpen)}>
          <div className={s.railSummary}>
            <button
              type="button"
              className={s.railToggle}
              aria-expanded={railOpen}
              aria-controls="onb-rail-steps"
              onClick={() => setRailOpen((open) => !open)}
            >
              <span className={s.railToggleText}>
                <span className={s.railToggleCount}>{finished ? 'All done' : stepCountLabel}</span>
                <span className={s.railToggleName}>
                  {finished ? 'Welcome aboard' : railRows[stepIndex]?.name}
                </span>
              </span>
              <span className={s.railToggleAction}>
                {railOpen ? 'Hide Steps' : 'Show Steps'}
                <FiChevronDown
                  className={clsx(s.railChevron, railOpen && s.railChevronOpen)}
                  aria-hidden="true"
                />
              </span>
            </button>
            <ProgressBar
              size="sm"
              value={intent ? (shownStep / shownTotal) * 100 : 20}
              label="Onboarding progress"
            />
          </div>

          <div id="onb-rail-steps" className={s.railStepsWrap}>
            <ol className={s.stepList}>
              <li className={clsx(s.stepItem, s.stepItemDone)}>
                <span className={s.stepHit}>
                  <span className={s.stepBullet}>
                    <FiCheck />
                  </span>
                  <span className={s.stepText}>
                    <span className={s.stepName}>Account created</span>
                    <span className={s.stepHint}>You’re signed in</span>
                  </span>
                </span>
              </li>
              {railRows.map((row, idx) => {
                const done = finished || idx < stepIndex;
                const active = !finished && idx === stepIndex;
                const clickable = done && !finished;
                return (
                  <li
                    key={row.id}
                    className={clsx(
                      s.stepItem,
                      done && s.stepItemDone,
                      active && s.stepItemActive,
                      !done && !active && s.stepItemAhead,
                    )}
                    style={{ '--_row': idx } as CSSProperties}
                  >
                    <button
                      type="button"
                      className={s.stepHit}
                      disabled={!clickable}
                      aria-current={active ? 'step' : undefined}
                      onClick={() => {
                        setTriedStep(null);
                        setStepIndex(idx);
                      }}
                    >
                      <span className={s.stepBullet}>
                        {done ? <FiCheck key="done" /> : <span key="num">{idx + 2}</span>}
                      </span>
                      <span className={s.stepText}>
                        <span className={s.stepName}>{row.name}</span>
                        <span className={s.stepHint}>{row.hint}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
              {!intent && (
                <li className={clsx(s.stepItem, s.stepItemAhead)}>
                  <span className={s.stepHit}>
                    <span className={s.stepBullet} aria-hidden="true" />
                    <span className={s.stepText}>
                      <span className={s.stepName}>Next steps</span>
                      <span className={s.stepHint}>Shown after you choose</span>
                    </span>
                  </span>
                </li>
              )}
            </ol>
          </div>

          <div className={s.identity}>
            <Avatar firstName={identity.firstName} lastName={identity.lastName} size={48} />
            <div className={s.identityText}>
              <span className={s.identityName}>
                {identity.firstName} {identity.lastName}
              </span>
              <span className={s.identityEmail}>{identity.email}</span>
            </div>
          </div>
        </aside>

        <main className={`${s.editor} ${isSubscription ? s.editorSub : ''}`}>
          {finished && joined ? (
            <div className={s.editorBody}>
              <div className={s.stage}>
                <div className={s.crewFinish}>
                  <div className={s.finishMark}>
                    <ConfettiCanvas fireKey={confettiKey} />
                    <SuccessCheck size={88} label="Onboarding complete" />
                  </div>
                  <div className={s.finishHead}>
                    <h2 className={s.finishTitle}>
                      {identity.firstName
                        ? `Welcome aboard, ${identity.firstName}!`
                        : 'Welcome aboard!'}
                    </h2>
                    <p className={s.finishLede}>
                      You joined {joined.serviceName ?? 'the vessel'} as {formatRole(joined.role)}.
                    </p>
                  </div>
                  <Button className={s.finishCta} onClick={goToDashboard}>
                    Go to My Dashboard
                    <BiChevronRight aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </div>
          ) : finished ? (
            <div className={s.editorBody}>
              <div className={s.stage}>
                {settingUp ? (
                  <FinishSetup
                    tasks={setupTasks}
                    onDone={() => {
                      setSettingUp(false);
                      setConfettiKey(`done-${intent}`);
                    }}
                  />
                ) : (
                  <div className={s.crewFinish}>
                    <div className={s.finishMark}>
                      <ConfettiCanvas fireKey={confettiKey} />
                      <SuccessCheck size={88} label="Onboarding complete" />
                    </div>
                    <div className={s.finishHead}>
                      <h2 className={s.finishTitle}>
                        {identity.firstName
                          ? `Welcome aboard, ${identity.firstName}!`
                          : 'Welcome aboard!'}
                      </h2>
                      {/* No promise of job alerts: there are none yet. What is true is that
                          the profile is public, so captains searching for these roles find it. */}
                      <p className={s.finishLede}>{finishLede}</p>
                    </div>
                    <ul className={s.readyList}>
                      {finishReadyLines.map((line, i) => (
                        <li key={line} style={{ '--_row': i } as CSSProperties}>
                          <span className={s.readyTick} aria-hidden="true">
                            <BiCheck />
                          </span>
                          {line}
                        </li>
                      ))}
                    </ul>
                    {finishNext.length > 0 && (
                      <section className={s.nextSection} aria-labelledby="onb-finish-next">
                        <h3 className={s.nextTitle} id="onb-finish-next">
                          What would you like to do first?
                        </h3>
                        <NextStepTiles
                          items={finishNext}
                          onPick={(id) =>
                            void leaveOnboarding(finishNext.find((step) => step.id === id)?.to)
                          }
                        />
                      </section>
                    )}
                    <Button className={s.finishCta} onClick={goToDashboard}>
                      Go to My Dashboard
                      <BiChevronRight aria-hidden="true" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className={s.editorBody}>
                <div className={clsx(s.stage, isSubscription && s.stageWide)}>
                  <div className={s.stageHead}>
                    <span className={s.stepCount}>{stepCountLabel}</span>
                    {guestDietGroup && (
                      <TextLink onClick={() => void skipGuestDietStep()}>Skip for now</TextLink>
                    )}
                  </div>
                  {stepViews[currentStepId]()}
                </div>
              </div>

              {stepIndex > 0 && (
                <div className={clsx(s.footer, isSubscription && s.footerWide)}>
                  <ButtonGhost className={s.backBtn} onClick={goBack}>
                    <BiChevronLeft aria-hidden="true" /> Back
                  </ButtonGhost>
                  {!isChoiceStep && (
                    <div className={s.footerRight}>
                      {error && <span className={s.footerNote}>{error}</span>}
                      {isTrialStep && (
                        <ButtonGhost
                          className={s.trialCompare}
                          onClick={() => window.open(PRICING_URL, '_blank', 'noopener,noreferrer')}
                        >
                          Compare Plans <FiArrowUpRight aria-hidden="true" />
                        </ButtonGhost>
                      )}
                      <Button
                        className={s.nextBtn}
                        onClick={goNext}
                        disabled={
                          (!canNext && !explainsMissing) ||
                          submitting ||
                          vesselEmail.checking ||
                          joinByCode.isPending
                        }
                      >
                        {submitting ? 'Saving…' : joinByCode.isPending ? 'Joining…' : ctaLabel}
                        {(!isSubscription || ownerHasPlan) && !submitting && (
                          <BiChevronRight aria-hidden="true" />
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </section>
      <EmailConflictModal
        open={vesselEmail.open}
        email={vesselEmail.conflictEmail}
        onClose={vesselEmail.close}
        onContinue={async (edited) => {
          if (await vesselEmail.resolve(edited, form.vesselName)) setStepIndex((i) => i + 1);
        }}
        isChecking={vesselEmail.checking}
      />
    </BasicLayout>
  );
}
